import React, { Dispatch, RefObject, SetStateAction } from 'react'
import './SPTextEditor.css'
import useSPTextEditor, { IEditorInput } from './useSPTextEditor'
import {
  EmojiPicker,
  HtmlEditor,
  Inject,
  Link,
  QuickToolbar,
  RichTextEditorComponent,
  Toolbar,
  ToolbarSettingsModel,
} from '@syncfusion/ej2-react-richtexteditor'
import { CHAT_LEFT_BUTTONS } from '@constants/Message'
import { MentionComponent } from '@syncfusion/ej2-react-dropdowns'
import { generateSvg } from '@utils/svg.util'
import { VARIABLE_LIST } from '@constants/Marketing'

export interface ISPTEProps {
  editorInput: IEditorInput
  isEditing?: boolean
  onSend: (plainText: string, htmlContent: string) => void
  setEditorInput: Dispatch<SetStateAction<IEditorInput>>
  toolbarRef?: RefObject<RichTextEditorComponent>
}

const SPTextEditor: React.FC<ISPTEProps> = ({
  editorInput,
  isEditing,
  onSend,
  setEditorInput,
  toolbarRef,
}) => {
  const { ...hooks } = useSPTextEditor(
    editorInput,
    setEditorInput,
    onSend,
    toolbarRef ?? null,
    isEditing,
  )

  const toolbarSettings: ToolbarSettingsModel = {
    items: CHAT_LEFT_BUTTONS.filter((b) => b.key !== 'ai').map((data) => {
      if (data.type === 'builtin') {
        return data.icon
      } else {
        return {
          template: generateSvg(
            data.key,
            data.icon,
            data.type === 'ti' ? 24 : 16,
          ),
          undo: true,
          tooltipText: data.text,
          click: () => {
            hooks.handleToolbarActions(data.key)
          },
        }
      }
    }),
    enable: true,
  }

  return (
    <>
      <div className="side-panel-editor-container">
        <div className="editor-wrapper">
          <RichTextEditorComponent
            id="sp-target-input"
            ref={hooks.toolbarRef}
            toolbarSettings={toolbarSettings}
            actionBegin={hooks.onActionBegin}
            value={editorInput.htmlContent}
            enableTabKey={true}
            enterKey="BR"
            cssClass="message-rte-tooltip"
            className="side-panel-text-editor"
            placeholder={isEditing ? 'Edit message' : 'Type a message'}
            change={hooks.onTextChange}
            created={hooks.onCreated}
            onClick={hooks.saveCaretPosition}
            focus={hooks.saveCaretPosition}
          >
            <Inject
              services={[Toolbar, Link, HtmlEditor, QuickToolbar, EmojiPicker]}
            />
          </RichTextEditorComponent>
          <div className="chat-right-buttons">
            <button
              type="button"
              key="send"
              data-bs-toggle="tooltip"
              title="Send"
              id="custom-send-button"
              className="unstyled-button crb-button crb-send-button"
              aria-label="Send"
              onClick={hooks.handleSend}
            >
              <span className="mgc_send_fill send-icon rb-icon" />
            </button>
          </div>
          {isEditing && <span className="edit-label">Editing</span>}
        </div>
      </div>
      <div className="tec-mention-comp">
        <MentionComponent
          target="#sp-target-input"
          ref={hooks.variableRef}
          dataSource={VARIABLE_LIST}
          showMentionChar={false}
          mentionChar="("
          cssClass="variable-component-text"
          fields={{ text: 'text', value: 'value' }}
          suffixText={'&nbsp;'}
          opened={() => hooks.setVariablePopupOpen(true)}
          closed={() => hooks.setVariablePopupOpen(false)}
        />
        <MentionComponent
          target="#sp-target-input"
          ref={hooks.mentionRef}
          dataSource={hooks.peopleList}
          showMentionChar={true}
          mentionChar="@"
          cssClass="mention-component-text"
          fields={{ text: 'name', value: 'name' }}
          suffixText={'&nbsp;'}
        />
      </div>
    </>
  )
}

export default SPTextEditor
